import React from 'react';
import PropTypes from 'prop-types';
import { compose, onlyUpdateForKeys } from 'recompose';
import { hot } from 'react-hot-loader/root';

import { Helmet } from 'react-helmet-async';
import truncate from 'lodash/truncate';
import has from 'lodash/has';

import config from 'config/config';
import env from 'config/env';
import resize from 'tools/img/resize';

import { setResponseHeader } from 'app/pages/app/app.actions';
import { appLocaleType } from 'app/pages/app/app.types';
import locationShape from 'tools/prop-types/location-shape';

const resize1280 = resize(1280);

const hoc = compose(
  hot,
  onlyUpdateForKeys(['title']),
);

const propTypes = {
  dispatch: PropTypes.func.isRequired,
  title: PropTypes.string,
  addProjectName: PropTypes.bool,
  description: PropTypes.string,
  keywords: PropTypes.string,
  image: PropTypes.string,
  images: PropTypes.arrayOf(PropTypes.object),
  noindex: PropTypes.bool,
  none: PropTypes.bool,
  canonical: PropTypes.string,
  locale: appLocaleType.isRequired,
  location: locationShape.isRequired,
};

const defaultProps = {
  title: undefined,
  addProjectName: true,
  description: undefined,
  keywords: undefined,
  image: undefined,
  images: undefined,
  noindex: false,
  none: false,
  canonical: undefined,
};

const HelmetDisplayerComponent = ({
  title,
  description,
  keywords,
  image,
  images,
  noindex,
  addProjectName,
  none,
  canonical,
  dispatch,
  locale,
  location,
  // ampLink,
}) => {
  const { clientAddress } = config;
  const projectName = addProjectName && locale !== 'ru';
  const titleString = title ? `${title}${projectName ? ' • RAWG' : ''}` : '';
  const descriptionString = description ? description.replace(/<\/?[^>]+(>|$)/g, ' ') : '';
  const truncatedDescription = truncate(descriptionString, {
    length: 160,
    separator: /,?\.* +/,
  });

  const isPageQuery = has(location.query, 'page');

  if (env.isServer() && noindex) {
    dispatch(setResponseHeader('X-Robots-Tag', 'noindex'));
  }

  if (env.isServer() && none) {
    dispatch(setResponseHeader('X-Robots-Tag', 'none'));
  }

  return (
    <>
      {titleString && (
        <Helmet>
          <title>{titleString}</title>
          <meta property="og:title" content={titleString} />
          <meta property="twitter:title" content={titleString} />
        </Helmet>
      )}

      {descriptionString && (
        <Helmet>
          <meta name="description" content={truncatedDescription} />
          <meta property="og:description" content={truncatedDescription} />
          <meta property="twitter:description" content={truncatedDescription} />
        </Helmet>
      )}

      {keywords && (
        <Helmet>
          <meta name="keywords" content={keywords} />
        </Helmet>
      )}

      {(image || images) && (
        <Helmet>
          <meta property="og:image" content={images ? images['1200'] : resize1280(image)} />
          <meta property="twitter:image" content={images ? images['1024'] : resize1280(image)} />
          <link rel="image_src" href={images ? images['1200'] : resize1280(image)} />
        </Helmet>
      )}

      {noindex && !isPageQuery && (
        <Helmet>
          <meta name="robots" content="noindex" />
        </Helmet>
      )}
      {isPageQuery && (
        <Helmet>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
      )}
      {none && (
        <Helmet>
          <meta name="robots" content="none" />
        </Helmet>
      )}
      {canonical && (
        <Helmet>
          <link rel="canonical" href={`${clientAddress[locale]}${canonical}`} />
        </Helmet>
      )}
      {/* {ampLink && (
          <Helmet>
            <link rel="amphtml" href={`${clientAddress}${ampLink}`} />
          </Helmet>
        )} */}
    </>
  );
};

HelmetDisplayerComponent.propTypes = propTypes;
HelmetDisplayerComponent.defaultProps = defaultProps;

const HelmetDisplayer = hoc(HelmetDisplayerComponent);

export default HelmetDisplayer;
